import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaSync } from "react-icons/fa";
import axiosInstance from "../../components/api/axiosInstance";
import { getAllowedSidebarPages, getCurrentUserRole } from "../../utils/roleAccess";
import ItemDrawer from "./ItemDrawer";
import { Avatar, Empty, PageHead, StatusPill } from "./WorkUi";
import { errorMessage, fmtShortDate, relativeDay, statusLabel, toISODate } from "./workApi";
import "../dm/dm.css";
import "./work.css";

const sel = { border: "1px solid #cbd5e1", borderRadius: 8, padding: "7px 10px", minHeight: 38, background: "#fff" };

function FilterBar({ children }) {
  return <div className="wk-cal-filters">{children}</div>;
}

/** Deliverables across projects (team-scoped) with filters, for managers to follow up and decide. */
function DeliverablesTable({ api }) {
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({ status: "open", area_id: "", user_id: "", late: "", needs_update: "", search: "", due_to: "" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [openItem, setOpenItem] = useState(null);
  const [sort, setSort] = useState({ key: "Target_Date", dir: 1 });

  useEffect(() => {
    api.meta().then(setMeta).catch(() => null);
  }, [api]);

  const load = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v != null));
      setData(await api.listItems(params));
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load deliverables"));
    }
  }, [api, filters]);

  useEffect(() => {
    const t = setTimeout(load, filters.search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, filters.search]);

  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.type === "checkbox" ? (e.target.checked ? "1" : "") : e.target.value }));

  const rows = useMemo(() => {
    const list = [...(data?.items || [])];
    list.sort((a, b) => {
      const av = a[sort.key] ?? "";
      const bv = b[sort.key] ?? "";
      if (av === bv) return a.Item_Id - b.Item_Id;
      if (av === "") return 1;
      if (bv === "") return -1;
      return (av > bv ? 1 : -1) * sort.dir;
    });
    return list;
  }, [data, sort]);

  const th = (key, label) => (
    <th>
      <button
        type="button"
        className="dmp-link"
        style={{ fontWeight: 700, fontSize: 12, color: "inherit", textTransform: "uppercase" }}
        onClick={() => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }))}
        aria-label={`Sort by ${label}`}
      >
        {label}
        {sort.key === key ? (sort.dir > 0 ? " ▲" : " ▼") : ""}
      </button>
    </th>
  );

  return (
    <>
      <FilterBar>
        <input value={filters.search} onChange={set("search")} placeholder="Search title" aria-label="Search title" style={{ ...sel, minWidth: 200 }} />
        <select value={filters.status} onChange={set("status")} aria-label="Status" style={sel}>
          <option value="open">Open</option>
          <option value="all">All</option>
          <option value="closed">Posted / done</option>
          {(meta?.statuses || []).map((s) => (
            <option key={s} value={s}>
              {statusLabel(s)}
            </option>
          ))}
        </select>
        <select value={filters.area_id} onChange={set("area_id")} aria-label="Area" style={sel}>
          <option value="">All areas</option>
          {(meta?.areas || []).map((a) => (
            <option key={a.Area_Id} value={a.Area_Id}>
              {a.Area_Name}
            </option>
          ))}
        </select>
        <select value={filters.user_id} onChange={set("user_id")} aria-label="Person" style={sel}>
          <option value="">Everyone</option>
          {(meta?.people || []).map((p) => (
            <option key={p.User_Id} value={p.User_Id}>
              {p.Name}
            </option>
          ))}
        </select>
        <label className="wk-chip" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={!!filters.late} onChange={set("late")} /> Late only
        </label>
        <label className="wk-chip" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={!!filters.needs_update} onChange={set("needs_update")} /> No update today
        </label>
        <label className="wk-muted" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          Due by
          <input type="date" value={filters.due_to} onChange={set("due_to")} style={sel} />
        </label>
      </FilterBar>
      {data ? (
        <div className="wk-person__counts" style={{ marginBottom: 10 }}>
          <span className="wk-count">{data.total} items</span>
          <span className="wk-count wk-count--red">{data.summary.late} late</span>
          <span className="wk-count wk-count--violet">{data.summary.in_review} waiting review</span>
          <span className="wk-count wk-count--amber">{data.summary.blocked} stuck</span>
          <span className="wk-count wk-count--amber">{data.summary.needs_update} no update today</span>
        </div>
      ) : null}
      {error ? <div className="dmp-error">{error}</div> : null}
      {!data ? (
        <div className="wk-muted">Loading…</div>
      ) : rows.length ? (
        <div style={{ overflowX: "auto" }}>
          <table className="wk-table">
            <thead>
              <tr>
                {th("Title", "Deliverable")}
                {th("Project_Name", "Project")}
                {th("Area_Name", "Area")}
                {th("Executor_Name", "Doing it")}
                {th("Status", "Status")}
                {th("Target_Date", "Due")}
                {th("Planned_Post_Date", "Post on")}
                {th("Last_Update_On", "Last update")}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.Item_Id}
                  onClick={() => setOpenItem(r.Item_Id)}
                  onKeyDown={(e) => (e.key === "Enter" ? setOpenItem(r.Item_Id) : null)}
                  tabIndex={0}
                  style={{ cursor: "pointer", background: r.Is_Overdue ? "#fef2f2" : undefined }}
                  aria-label={`Open ${r.Title}`}
                >
                  <td>
                    <strong>{r.Title}</strong>
                    <div className="wk-muted" style={{ fontSize: 12 }}>
                      {r.Item_Type}
                    </div>
                  </td>
                  <td>
                    {r.Project_Name || "—"}
                    {r.Tenant_Name ? <div className="wk-muted" style={{ fontSize: 12 }}>{r.Tenant_Name}</div> : null}
                  </td>
                  <td>{r.Area_Name || "—"}</td>
                  <td>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Avatar name={r.Executor_Name} size={22} /> {r.Executor_Name || "Unassigned"}
                    </span>
                  </td>
                  <td>
                    <StatusPill status={r.Status} small />
                  </td>
                  <td className={r.Is_Overdue ? "wk-due is-late" : ""}>
                    {r.Target_Date ? (
                      <>
                        {fmtShortDate(r.Target_Date)}
                        <div style={{ fontSize: 12, fontWeight: 500 }}>{relativeDay(r.Target_Date)}</div>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{r.Planned_Post_Date ? fmtShortDate(r.Planned_Post_Date) : "—"}</td>
                  <td>{r.Updated_Today ? "Today" : r.Last_Update_On ? fmtShortDate(r.Last_Update_On) : <span className="wk-muted">Never</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty title="Nothing matches these filters" />
      )}
      {openItem ? <ItemDrawer api={api} itemId={openItem} meta={meta} onClose={() => setOpenItem(null)} onChanged={load} /> : null}
    </>
  );
}

const KIND_LABELS = { task: "Task", post: "DM post", followup: "Follow-up" };

/** Assigned PMS tasks, DM posts and lead follow-ups due by a date (the former My Day list), as a table. */
function TasksFollowUpsTable() {
  const navigate = useNavigate();
  const [date, setDate] = useState(toISODate());
  const [kind, setKind] = useState("");
  const [lateOnly, setLateOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/my-day", { params: { date } });
      setData(res.data || {});
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load tasks and follow-ups"));
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const all = [
      ...(data.tasks || []).map((r) => ({ ...r, _kind: "task" })),
      ...(data.posts || []).map((r) => ({ ...r, _kind: "post" })),
      ...(data.followups || []).map((r) => ({ ...r, _kind: "followup" })),
    ];
    const q = search.trim().toLowerCase();
    return all
      .filter((r) => (!kind || r._kind === kind) && (!lateOnly || r.overdue) && (!q || String(r.title || "").toLowerCase().includes(q)))
      .sort((a, b) => (a.overdue === b.overdue ? String(a.due_date || "").localeCompare(String(b.due_date || "")) : a.overdue ? -1 : 1));
  }, [data, kind, lateOnly, search]);

  return (
    <>
      <FilterBar>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" aria-label="Search" style={{ ...sel, minWidth: 200 }} />
        <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Type" style={sel}>
          <option value="">All types</option>
          <option value="task">Tasks</option>
          <option value="post">DM posts</option>
          <option value="followup">Lead follow-ups</option>
        </select>
        <label className="wk-chip" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={lateOnly} onChange={(e) => setLateOnly(e.target.checked)} /> Late only
        </label>
        <label className="wk-muted" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          Due by
          <input type="date" value={date} onChange={(e) => setDate(e.target.value || toISODate())} style={sel} />
        </label>
        <button type="button" className="dmp-btn dmp-btn--ghost" onClick={load} aria-label="Refresh">
          <FaSync aria-hidden="true" />
        </button>
      </FilterBar>
      {error ? <div className="dmp-error">{error}</div> : null}
      {!data ? (
        <div className="wk-muted">Loading…</div>
      ) : rows.length ? (
        <div style={{ overflowX: "auto" }}>
          <table className="wk-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Type</th>
                <th>Status</th>
                <th>Due</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={`${r._kind}-${r.id || r.Followup_Id || r.Post_Id || r.Task_Id || i}`}
                  onClick={() => r.path && navigate(r.path)}
                  onKeyDown={(e) => (e.key === "Enter" && r.path ? navigate(r.path) : null)}
                  tabIndex={0}
                  style={{ cursor: r.path ? "pointer" : "default", background: r.overdue ? "#fef2f2" : undefined }}
                >
                  <td>
                    <strong>{r.title}</strong>
                    {r.subtitle ? <div className="wk-muted" style={{ fontSize: 12 }}>{r.subtitle}</div> : null}
                  </td>
                  <td>{KIND_LABELS[r._kind]}</td>
                  <td>{r.status || "—"}</td>
                  <td className={r.overdue ? "wk-due is-late" : ""}>
                    {r.due_date ? fmtShortDate(r.due_date) : "—"}
                    {r.overdue ? <div style={{ fontSize: 12 }}>Late</div> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty title="Nothing due">Tasks, DM posts and lead follow-ups assigned to you appear here.</Empty>
      )}
    </>
  );
}

const LEAD_COLUMNS = [
  { key: "newLeads", label: "New", status: "New" },
  { key: "newLeadsToday", label: "New today", status: "New", today: true },
  { key: "waiting", label: "Waiting for confirmation", status: "Waiting for Confirmation" },
  { key: "followupToday", label: "Follow-ups today", followupToday: true },
  { key: "nextFollowup", label: "Next follow-up", status: "Next Followup" },
  { key: "total", label: "Total", status: "All Statuses" },
];

/** Lead pipeline per lead type with clickable counts (opens the filtered lead list). */
function LeadsPipelineTable() {
  const navigate = useNavigate();
  const [modules, setModules] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axiosInstance
      .get("/dashboard/counts")
      .then((res) => setModules(res?.data?.leads?.modules || []))
      .catch((err) => setError(errorMessage(err, "Could not load leads")));
  }, []);

  const open = (mod, col) => {
    const today = toISODate();
    const digixFilter = {
      status: col.status || "All Statuses",
      datePreset: col.today ? "today" : "all",
      createdFrom: col.today ? today : "",
      createdTo: col.today ? today : "",
      nextFollowupDate: col.followupToday ? today : "",
    };
    navigate(mod.path || "/business-leads", { state: { digixFilter } });
  };

  if (error) return <div className="dmp-error">{error}</div>;
  if (!modules) return <div className="wk-muted">Loading…</div>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="wk-table">
        <thead>
          <tr>
            <th>Lead type</th>
            {LEAD_COLUMNS.map((c) => (
              <th key={c.key} style={{ textAlign: "right" }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {modules.map((m) => (
            <tr key={m.key}>
              <td>
                <strong>{m.title}</strong>
              </td>
              {LEAD_COLUMNS.map((c) => (
                <td key={c.key} style={{ textAlign: "right" }}>
                  <button type="button" className="dmp-link" onClick={() => open(m, c)} aria-label={`${m.title}: ${c.label}`}>
                    {Number(m[c.key] || 0).toLocaleString("en-IN")}
                  </button>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Work > Follow-ups: detailed tables to go through, filter and act on (high-level numbers stay on the Dashboard). */
function WorkFollowUps({ api }) {
  const [params, setParams] = useSearchParams();
  const role = getCurrentUserRole();
  const allowed = useMemo(() => new Set(getAllowedSidebarPages(role)), [role]);
  const tabs = [
    { key: "deliverables", label: "Deliverables" },
    api.isStaff ? { key: "tasks", label: "Tasks & follow-ups" } : null,
    api.isStaff && allowed.has("DigixLeads") ? { key: "leads", label: "Leads pipeline" } : null,
  ].filter(Boolean);
  const tab = tabs.some((t) => t.key === params.get("tab")) ? params.get("tab") : "deliverables";

  return (
    <div className="dmp wk">
      <PageHead title="Follow-ups" />
      <div className="dmp-tabs dmp-tabs--pills" role="tablist" aria-label="Follow-up tables" style={{ marginBottom: 12 }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`dmp-tab${tab === t.key ? " is-active" : ""}`}
            onClick={() => setParams({ tab: t.key }, { replace: true })}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="dmp-card">
        {tab === "deliverables" ? <DeliverablesTable api={api} /> : null}
        {tab === "tasks" ? <TasksFollowUpsTable /> : null}
        {tab === "leads" ? <LeadsPipelineTable /> : null}
      </div>
    </div>
  );
}

export default WorkFollowUps;
